import { Attrs, EventListeners, MinzeElement, Reactive } from 'minze'
import { Font, MODALS } from '../../constants'
import { storeInCache } from '../../modules/cache'
import { Locale } from '../../modules/locale'
import MainModule from '../../modules/main'
import { Routing } from '../../modules/routing'

Locale.getInstance().registerKeySet('fr', 'lvcAccountNav', {
  user: {
    title: 'Mon compte',
    culture: 'Mon profil culturel',
    favorites: 'Mes favoris',
    security: 'Connexion et sécurité',
    personal: 'Mes infos personnelles',
    notification: 'Notifications',
  },
  partner: {
    title: 'Mon compte professionnel',
    createEvent: 'Soumettre un événement',
    createExhibition: 'Soumettre une exposition',
    createFestival: 'Soumettre un festival',
    createContribution: 'Soumettre une contribution',
    myDrafts: 'Mes brouillons',
    partnerProfile: 'Profil partenaire',
  },
  completeProfileTitle: 'La Vitrine Culturelle est une organisation à but non lucratif québécoise',
  completeProfileSubtitle:
    "En créant un profil vous recevrez des suggestions d'activités selon vos goûts.",
  completeProfileText:
    "Vos renseignements personnels resteront sous notre garde, ne seront partagés avec personne et ne seront jamais vendus. Ils ne serviront qu'à accomplir notre mission : promouvoir et vous faire découvrir des activités qui se déroulent au Québec. Vous pourrez en tout temps demander de supprimer ou de modifier votre profil Vitrine en vous connectant à votre compte.",
  logout: 'Se déconnecter',
})
Locale.getInstance().registerKeySet('en', 'lvcAccountNav', {
  user: {
    title: 'My account',
    culture: 'My cultural profile',
    favorites: 'My favorites',
    security: 'Sign in and security',
    personal: 'My personal information',
    notification: 'Notifications',
  },
  partner: {
    title: 'My professional account',
    createEvent: 'Create an event',
    createExhibition: 'Create an exhibition',
    createFestival: 'Create a festival',
    myDrafts: 'My drafts',
    partnerProfile: 'Partner profile',
  },
  completeProfileTitle: 'La Vitrine Culturelle is a non-profit organization in Quebec',
  completeProfileSubtitle:
    'By creating a profile, you will receive activity suggestions based on your preferences.',
  completeProfileText:
    'Your personal information will remain confidential, will not be shared with anyone, and will never be sold. It will only be used to fulfill our mission: to promote and help you discover activities in Quebec. You can request to delete or modify your Vitrine profile at any time by logging into your account.',
  logout: 'Sign out',
})

interface MenuItem {
  title: string
  url: string
  icon: string
  proFeature: boolean
  gtmLinkText?: string // Optional GTM event for tracking
  id?: string
}

export interface LvcAccountNav {
  userMenuItems: Array<MenuItem>
  partnerMenuItems: Array<MenuItem>
  isPartner: boolean
  showDropdownMenu: boolean
  completeProfile: boolean
  disableUserMenu?: boolean
  isInB2bApp?: boolean
  onLogoutRedirectUrl?: string
}

export class LvcAccountNav extends MinzeElement {
  _authHandler: any = null

  attrs: Attrs = [
    'complete-profile', // If it's true, we hide the nav and show some text
    'disable-user-menu', // If true, we disable the user menu (for partners who don't have access)
    'is-in-b2b-app', // URL to redirect after logout
    'on-logout-redirect-url', // URL to redirect after logout
  ]

  reactive: Reactive = [
    ['userMenuItems', []],
    ['partnerMenuItems', []],
    ['isPartner', false],
    ['showBecomePartnerButton', true],
    ['showDropdownMenu', false],
    ['showUserDropdownMenu', true],
    ['showPartnerDropdownMenu', false],
  ]

  handleAuthChanged = async (data: any): Promise<void> => {
    const mainModule = MainModule.getInstance()
    const isPartner = await mainModule.getAuth().isPartner()
    const isPartnerInProcess = await mainModule.getAuth().isPartnerInProcess()
    this.isPartner = isPartner

    if (data?.isLoggedIn && (isPartner || isPartnerInProcess)) {
      this.showBecomePartnerButton = false
    }
  }

  toggleDropdown = () => {
    this.showDropdownMenu = !this.showDropdownMenu
  }

  toggleUserDropdown = () => {
    this.showUserDropdownMenu = !this.showUserDropdownMenu
  }

  togglePartnerDropdown = () => {
    this.showPartnerDropdownMenu = !this.showPartnerDropdownMenu
  }

  closeDropdown = () => {
    this.showDropdownMenu = false
  }

  handleLogout = async () => {
    if (this.isInB2bApp) {
      const mainModule = MainModule.getInstance()
      await mainModule.getAuth().b2bLogout(this.onLogoutRedirectUrl)
      return
    }
    const mainModule = MainModule.getInstance()
    storeInCache('shouldGoToLastPage', true, 10000)
    await mainModule.getAuth().logout()
    //@TODO handle redirect to home/last link (not profile)
    mainModule.hideModal(MODALS.PROFILE_MODAL)
  }

  trackProfileCultureClick = async () => {
    await MainModule.getInstance().analyticsTrack('cta_mon_profil', {
      emplacement: 'mon_compte_nav',
    })
  }

  trackProfileFavoritesClick = async () => {
    await MainModule.getInstance().analyticsTrack('cta_mes_favoris', {
      emplacement: 'mon_compte_nav',
    })
  }

  trackProfileNotificationClick = async () => {
    await MainModule.getInstance().analyticsTrack('cta_notifications', {
      emplacement: 'mon_compte_nav',
    })
  }

  async onReady(): Promise<void> {
    const mainModule = MainModule.getInstance()
    const lang = mainModule.getLang()

    if (this.disableUserMenu) {
      this.showUserDropdownMenu = false
      this.showPartnerDropdownMenu = true
      this.showDropdownMenu = false // mobile dropdown
    }

    this.userMenuItems = [
      {
        id: 'profile-culture',
        title: Locale.getKey('lvcAccountNav.user.culture'),
        url: Routing.ROUTES[lang].profileCulture,
        icon: '<lvc-account-theater-icon></lvc-account-theater-icon>',
        proFeature: false,
        gtmLinkText: Locale.getKey('lvcAccountNav.user.culture'),
      },
      {
        id: 'profile-favorites',
        title: Locale.getKey('lvcAccountNav.user.favorites'),
        url: Routing.ROUTES[lang].profileFavorites,
        icon: '<lvc-account-heart-icon></lvc-account-heart-icon>',
        proFeature: false,
        gtmLinkText: Locale.getKey('lvcAccountNav.user.favorites'),
      },
      {
        title: Locale.getKey('lvcAccountNav.user.security'),
        url: Routing.ROUTES[lang].profileSecurity,
        icon: '<lvc-account-key-icon></lvc-account-key-icon>',
        proFeature: false,
        gtmLinkText: Locale.getKey('lvcAccountNav.user.security'),
      },
      {
        title: Locale.getKey('lvcAccountNav.user.personal'),
        url: Routing.ROUTES[lang].profilePersonal,
        icon: '<lvc-account-lock-icon></lvc-account-lock-icon>',
        proFeature: false,
        gtmLinkText: Locale.getKey('lvcAccountNav.user.personal'),
      },
      {
        id: 'profile-notification-sidebar',
        title: Locale.getKey('lvcAccountNav.user.notification'),
        url: Routing.ROUTES[lang].profileNotification,
        icon: '<lvc-account-bell-icon></lvc-account-bell-icon>',
        proFeature: false,
        gtmLinkText: Locale.getKey('lvcAccountNav.user.notification'),
      },
    ]

    this.partnerMenuItems = [
      {
        title: Locale.getKey('lvcAccountNav.partner.createEvent'),
        url: Routing.ROUTES[lang].partnerCreateEvent,
        icon: '<lvc-create-event-icon data-width="30px" data-height="30px"></lvc-create-event-icon>',
        proFeature: true,
      },
      {
        title: Locale.getKey('lvcAccountNav.partner.createExhibition'),
        url: Routing.ROUTES[lang].partnerCreateExhibition,
        icon: '<lvc-create-event-icon data-width="30px" data-height="30px"></lvc-create-event-icon>',
        proFeature: true,
      },
      {
        title: Locale.getKey('lvcAccountNav.partner.createFestival'),
        url: Routing.ROUTES[lang].partnerCreateFestival,
        icon: '<lvc-create-event-icon data-width="30px" data-height="30px"></lvc-create-event-icon>',
        proFeature: true,
      },
      {
        title: Locale.getKey('lvcAccountNav.partner.createContribution'),
        url: Routing.ROUTES[lang].partnerCreateContribution,
        icon: '<lvc-create-contribution-icon data-width="30px" data-height="30px"></lvc-create-contribution-icon>',
        proFeature: true,
      },
      {
        title: Locale.getKey('lvcAccountNav.partner.myDrafts'),
        url: Routing.ROUTES[lang].partnerDrafts,
        icon: '<lvc-event-draft-icon data-width="30px" data-height="30px"></lvc-event-draft-icon>',
        proFeature: true,
      },
      {
        title: Locale.getKey('lvcAccountNav.partner.partnerProfile'),
        url: Routing.ROUTES[lang].partnerProfile,
        icon: '<lvc-partner-profile-icon data-width="30px" data-height="30px"></lvc-partner-profile-icon>',
        proFeature: true,
      },
    ]

    this._authHandler = this.handleAuthChanged.bind(this)
    mainModule.getEE().on('lvc:user_state_changed', this._authHandler.bind(this))
    this.handleAuthChanged({
      isLoggedIn: await mainModule.getAuth().isLoggedIn(),
    })
  }

  html = () => `
    <div id="dropdown-overlay"></div>
    <div class="account-nav-trigger-wrapper">
      <button id="mobile-user-dropdown-trigger" class="account-nav-trigger">
        <lvc-hand-drawn-box variant="button2">
          <div class="account-nav-trigger-inner">
            <span>${Locale.getKey('lvcAccountNav.user.title')}</span>
            <lvc-chevron-down-icon width="16px" height="16px" class="mobile-chevron"></lvc-chevron-down-icon>
          </div>
        </lvc-hand-drawn-box>
      </button>

      

    <div class="account-nav-content">
      <lvc-hand-drawn-box variant="box2">

  <!-- Complete profile -->
  ${
    this.completeProfile
      ? /*html*/ `
      <div class="account-nav-complete-profile">
        <h2 class="account-nav-title-complete-profile">${Locale.getKey('lvcAccountNav.completeProfileTitle')}</h2>
        <h3 class="account-nav-subtitle">${Locale.getKey('lvcAccountNav.completeProfileSubtitle')}</h3> 
        <p class="account-nav-text">${Locale.getKey('lvcAccountNav.completeProfileText')}</p> 
      </div>`
      : /*html*/ `
        <nav class="account-nav">
            <!-- Partner menu -->
            ${
              this.isPartner && this.isInB2bApp
                ? /*html*/ `
           <div class="desktop-partner-menu-wrapper">
              ${
                this.disableUserMenu
                  ? ''
                  : /*html*/ ` <div class="account-nav-header partner-header">
                <h2 class="account-nav-title">
                  ${Locale.getKey('lvcAccountNav.partner.title')}
                </h2>
                <lvc-chevron-down-icon width="16px" height="16px" class="partner-chevron"></lvc-chevron-down-icon>
              </div>`
              }

              <ul class="account-nav-menu partner-menu">
                ${this.partnerMenuItems
                  .map(item => {
                    if (!item.proFeature || this.isPartner) {
                      return /*html*/ `
                    <li>
                        <lvc-account-nav-item
                            label="${item.title}"
                            url="${item.url}">
                            <span slot="icon">${item.icon}</span>
                        </lvc-account-nav-item>
                      </li>
                    `
                    }
                  })
                  .join('')}
              </ul>
          </div>
            `
                : ''
            }
        ${
          this.disableUserMenu
            ? ''
            : /*html*/ `
        <!-- User menu -->
          <div class="account-nav-header user-header">
            <h2 class="account-nav-title">
              ${Locale.getKey('lvcAccountNav.user.title')}
            </h2>
            <lvc-chevron-down-icon width="16px" height="16px" class="user-chevron"></lvc-chevron-down-icon>
          </div>
            <ul class="account-nav-menu user-menu">
              ${this.userMenuItems
                .map(item => {
                  return /*html*/ `
                  <li>
                      <lvc-account-nav-item
                          id="${item.id || ''}"
                          label="${item.title}"
                          url="${item.url}"
                          gtm-link-text="${item.gtmLinkText}">
                          <span slot="icon">${item.icon}</span>
                      </lvc-account-nav-item>
                    </li>
                  `
                })
                .join('')}
            </ul>
          `
        }
          <div class="account-nav-footer">
            <button id="btn-logout">${Locale.getKey('lvcAccountNav.logout')}</button>
          </div>
        </nav> `
  }
      </lvc-hand-drawn-box>
    </div>
  `

  eventListeners: EventListeners = [
    ['#dropdown-overlay', 'click', this.closeDropdown.bind(this)],
    ['#mobile-user-dropdown-trigger', 'click', this.toggleDropdown.bind(this)],
    ['#btn-logout', 'click', this.handleLogout.bind(this)],
    ['#profile-culture', 'click', this.trackProfileCultureClick.bind(this)],
    ['#profile-favorites', 'click', this.trackProfileFavoritesClick.bind(this)],
    ['#profile-notification-sidebar', 'click', this.trackProfileNotificationClick],
    ['.user-header', 'click', this.toggleUserDropdown.bind(this)],
    ['.partner-header', 'click', this.togglePartnerDropdown.bind(this)],
  ]

  css = () => `
    :host {
      position: relative;
    }

    .account-nav-trigger {
      display: block;
      width: 100%;
      padding: 0;
      background: transparent;
      border: none;
      font-size: 1em;
    }

    .account-nav-trigger-inner {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 10px 20px;
      color: #1E007D;
      font-family: "Ppobjectsans", sans-serif;
      font-size: 1.125em;
      font-weight: 800;
    }

    .account-nav {
      display: flex;
      flex-direction: column;
      gap: 20px;
      width: 100%;
      padding: 20px 40px;
    }

    .account-nav-complete-profile {
      display: flex;
      flex-direction: column;
      width: 100%;
      padding: 20px 40px;
    }

    .account-nav-title, .account-nav-title-complete-profile {
      margin: 0;
      color: #1E007D;
      font-size: 1.125em;
      font-weight: 800;
      line-height: 120%;
    }

    .account-nav-title{
      user-select: none;
    }

    .desktop-partner-menu-wrapper {
      display: flex;
      flex-direction: column;
      gap: 20px;
    }

    .account-nav-menu {
      display: flex;
      flex-direction: column;
      gap: 20px;
      margin: 0;
      padding: 0;
      list-style: none;
    }

    .account-nav-footer {
      margin-top: 40px;
    }

    #btn-logout {
      background: transparent;
      border: none;
      color: #1E007D;
      font-family: "Ppobjectsans", sans-serif;
      font-size: 0.875em;
      font-weight: 500;
      line-height: 120%;
      text-decoration: underline;
      text-underline-offset: 4px;
      cursor: pointer;
    }

    #btn-logout:hover {
      text-decoration: none;
    }

    @media only screen and (max-width: 991px) {
      #dropdown-overlay {
        position: fixed;
        top: 0;
        left: 0;
        display: ${this.showDropdownMenu ? 'block' : 'none'};
        width: 100%;
        height: 100%;
        z-index: 99;
      }

      .account-nav-content {
        position: absolute;
        top: 100%;
        left: 0;
        display: ${this.showDropdownMenu ? 'block' : 'none'};
        width: 100%;
        margin-top: 10px;
        z-index: 100;
      }

      .account-nav-complete-profile {
        z-index: 100;
      }

      .account-nav-title {
        padding-bottom: 16px;
        border-bottom: 1px solid #1E007D;
      }

      .account-nav-title-complete-profile {
        font-size: 1.125em;
        font-weight: ${Font.weight.extraBold};
        color: #1E007D;
      }

      .account-nav-subtitle {
        color: #1E007D;
        font-size: 1rem;
      }

      .account-nav-text {
        font-size: 0.875em;
        color: #1E007D;
        font-weight: ${Font.weight.medium};
      }

      .user-chevron, .partner-chevron {
        display: none;
      }

      .account-nav-trigger-wrapper {
        display: flex;
        flex-direction: column;
        gap: 10px;
      }

      .mobile-chevron {
        display: flex;
        align-items: center;
        transform: rotate(${this.showDropdownMenu ? '180deg' : '0deg'});
      }
    }

    @media only screen and (min-width: 992px) {
      .account-nav-trigger {
        display: none;
      }

      .account-nav-content {
        display: block;
      }

      .account-nav {
        gap: 30px;
        width: 320px;
        min-height: 480px;
        padding: 40px;
      }

      .account-nav-complete-profile {
        width: 320px;
        min-height: 480px;
        padding: 40px;
      }

      .account-nav-title {
        font-size: 1.500em;
      }

      .account-nav-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        border-bottom: 1px solid #1E007D;
        cursor: pointer;
        padding: 6px 0px;
        user-select: none;
      }

      .user-menu {
        display: ${this.showUserDropdownMenu ? 'flex' : 'none'};
      }

      .partner-menu {
        display: ${this.showPartnerDropdownMenu ? 'flex' : 'none'};
      }

      .user-chevron {
        display: flex;
        align-items: center;
        transform: rotate(${this.showUserDropdownMenu ? '180deg' : '0deg'});
      }

      .partner-chevron {
        display: flex;
        align-items: center;
        transform: rotate(${this.showPartnerDropdownMenu ? '180deg' : '0deg'});
      }

      .account-nav-subtitle {
        font-size: 1rem;
        color: #1E007D;
      }

      .account-nav-text {
        font-size: 0.875em;
        color: #1E007D;
        font-weight: ${Font.weight.medium};
      }

      .account-nav-footer {
        margin-top: auto;
      }
    }
  `
}
